import React, { useState } from 'react';

const GoogleSignInButton = ({ className = '', onBeforeRedirect = null }) => {
    const [isRedirecting, setIsRedirecting] = useState(false);

    const getGoogleOAuthUrl = () => {
        // Google OAuth is a full-page redirect that chains through Google and back
        // to the backend. The /api CRA proxy can’t carry session cookies across
        // the redirect, so we always use an absolute backend URL here. Default to
        // prod so localhost can complete the flow without a local backend.
        const envUrl = process.env.REACT_APP_API_URL;
        const hostname = window.location.hostname;

        let backendUrl;
        if (envUrl && !envUrl.startsWith('/')) {
            backendUrl = envUrl;
        } else if (hostname.includes('ngrok')) {
            backendUrl = hostname.includes('blocknotify-app')
                ? 'https://blocknotify-api.ngrok-free.app/v1'
                : `https://${hostname.replace('-app.', '-api.')}/v1`;
        } else if (hostname !== 'localhost' && hostname !== '127.0.0.1' && process.env.NODE_ENV === 'development') {
            backendUrl = `http://${hostname}:4000/v1`;
        } else {
            backendUrl = 'https://api.blocknotify.com/v1';
        }

        const currentFrontendUrl = window.location.origin;
        return `${backendUrl}/auth/google?frontend_url=${encodeURIComponent(currentFrontendUrl)}`;
    };

    const handleGoogleSignIn = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Prevent double-clicks
        if (isRedirecting) return;
        setIsRedirecting(true);

        const googleOAuthUrl = getGoogleOAuthUrl();
        const next = new URLSearchParams(window.location.search).get('next');
        if (next && next.startsWith('/') && !next.startsWith('//')) {
            sessionStorage.setItem('oauthPostLoginNext', next);
        } else {
            sessionStorage.removeItem('oauthPostLoginNext');
        }
        if (typeof onBeforeRedirect === 'function') {
            onBeforeRedirect();
        }

        // Use replace to prevent back-button issues, with a small delay to ensure state updates
        setTimeout(() => {
            window.location.replace(googleOAuthUrl);
        }, 50);
    };

    return (
        <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isRedirecting}
            className={`w-full bg-white dark:bg-gray-800 text-content-primary py-3 px-4 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        >
            <div className="flex items-center justify-center space-x-3">
                {isRedirecting ? (
                    <>
                        <svg className="animate-spin h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="font-medium">Redirecting to Google...</span>
                    </>
                ) : (
                    <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                fill="#4285F4"
                            />
                            <path
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                fill="#34A853"
                            />
                            <path
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                fill="#FBBC05"
                            />
                            <path
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                fill="#EA4335"
                            />
                        </svg>
                        <span className="font-medium">Continue with Google</span>
                    </>
                )}
            </div>
        </button>
    );
};

export default GoogleSignInButton;

